/**
 * Vessel presentation: instances, attitude, wakes and damage state.
 *
 * This is a pure consumer of simulation state. It creates one instance per live vessel,
 * pushes position and attitude every frame, and disposes instances when the sim drops
 * them. It never writes back — if a ship is in the wrong place, the bug is in sim/.
 */

import type { Scene } from '@babylonjs/core/scene';
import type { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh';
import { Mesh as BabylonMesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { PBRMetallicRoughnessMaterial } from '@babylonjs/core/Materials/PBR/pbrMetallicRoughnessMaterial';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';

// Side-effect import: Babylon's tree-shaken build only wires up Mesh.createInstance when
// this module is present. Without it every vessel spawn throws and takes the render loop
// with it — which is exactly how it failed the first time.
import '@babylonjs/core/Meshes/instancedMesh';

import {
  buildVesselPrototypes,
  KRAKEN_ARM_CONTACT_PROGRESS,
  KRAKEN_ARM_WATER_CONTACTS,
  KRAKEN_VISUAL_BEAM,
  KRAKEN_VISUAL_LENGTH,
  krakenTentaclePathPoint,
  type VesselPrototypes,
} from './vesselMeshes';
import type { VesselMaterials } from './hullBuilder';
import { maxSimultaneousLightsFor } from '../lightBudget';
import { krakenArmOffset, krakenEyeOffset, type KrakenEyeState } from './krakenMotion';
import { tentacleRoll } from '../../content/tentacleMotion';
import { tentaclePose, tentacleCentrePoint } from '../../content/tentaclePose';
import { krakenRisePose } from '../../content/creatureScale';
import { KRAKEN } from '../../content/boss';
import {
  krakenPredationPose,
  krakenTentacleGrip,
  krakenTentacleTarget,
  type KrakenPredationPose,
  type KrakenTentacleGrip,
} from '../../content/krakenPredation';
import {
  krakenRigGrip,
  krakenRigWrapPoint,
  type KrakenRigGrip,
} from '../../content/krakenRig';
import { krakenPresence, type KrakenPresence } from './krakenPresence';
import { buildOrganicSkinRaster } from './organicSkin';
import { MistPlugin } from '../mistPlugin';
import { VESSEL_SPECS } from '../../content/vessels';
import type { Vessel, EntityId, VesselKind } from '../../sim/types';

/** How many points of history a wake keeps. At 6 Hz this is ~14 s of trail. */
const WAKE_POINTS = 84;
/** Seconds between wake samples. */
const WAKE_INTERVAL = 1 / 6;
/**
 * A normal shift never reaches this. Oil and debris cost two draws per trace, so the cap
 * bounds their worst case at 48 draws; added to the measured ~50-draw mobile scene that
 * remains inside the 100-draw contract even in an unusually destructive Endless watch.
 */
const WRECK_TRACE_LIMIT = 24;
const SLICK_SEGMENTS = 10;
/** Solid body is 70% opaque; the irregular outer ring fades that to clear water. */
const SLICK_OPACITY = 0.70;

/**
 * How long a wreck's oil stays on the water, seconds (Adam, 2026-07-30).
 *
 * They used to last the whole shift, which was the point of leaving wreckage at all — but
 * the sea ends up littered with cold patches from kills the player has long stopped
 * caring about, and a slick that never leaves stops reading as "something died here
 * recently" and starts reading as terrain. A minute is long enough to be the aftermath of
 * the fight you are still in.
 */
const SLICK_LIFETIME = 60;

/** Seconds of fade at the end of that life, so the slick thins out rather than blinking. */
const SLICK_FADE = 14;

function createOrganicSkinTextures(
  scene: Scene,
): readonly [DynamicTexture, DynamicTexture, DynamicTexture] {
  const size = 512;
  const raster = buildOrganicSkinRaster(size);
  const color = new DynamicTexture(
    'vesselOrganicSkinColor',
    { width: size, height: size },
    scene,
    true,
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  const normal = new DynamicTexture(
    'vesselOrganicSkinNormal',
    { width: size, height: size },
    scene,
    true,
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  const roughness = new DynamicTexture(
    'vesselOrganicSkinRoughness',
    { width: size, height: size },
    scene,
    true,
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  const colorContext = color.getContext();
  const normalContext = normal.getContext();
  const roughnessContext = roughness.getContext();
  const colorData = colorContext.getImageData(0, 0, size, size);
  const normalData = normalContext.getImageData(0, 0, size, size);
  const roughnessData = roughnessContext.getImageData(0, 0, size, size);

  colorData.data.set(raster.color);
  normalData.data.set(raster.normal);
  roughnessData.data.set(raster.metallicRoughness);

  colorContext.putImageData(colorData, 0, 0);
  normalContext.putImageData(normalData, 0, 0);
  roughnessContext.putImageData(roughnessData, 0, 0);
  color.update(false);
  normal.update(false);
  roughness.update(false);
  for (const texture of [color, normal, roughness]) {
    texture.wrapU = Texture.WRAP_ADDRESSMODE;
    texture.wrapV = Texture.WRAP_ADDRESSMODE;
  }
  color.gammaSpace = true;
  normal.gammaSpace = false;
  roughness.gammaSpace = false;
  normal.level = 0.78;
  return [color, normal, roughness];
}

interface VesselView {
  instance: InstancedMesh | BabylonMesh;
  kind: VesselKind;
  /** True while this creature is using the sub-1.5 km inspection mesh. */
  closeGeometry: boolean;
  /** Ring buffer of trailing wake samples in world space. */
  wake: Float32Array;
  wakeCount: number;
  wakeHead: number;
  wakeTimer: number;
  wakeMesh: BabylonMesh | null;
  contactMesh: BabylonMesh | null;
  submergedMesh: BabylonMesh | null;
  /** Unique, updatable geometry carried only by animated tentacles. */
  tentacle:
    | {
        basePositions: Float32Array;
        deformedPositions: Float32Array;
        deformationUvs: Float32Array;
        normals: number[];
        indices: number[];
      }
    | null;
  /** Unique geometry plus the limb id/progress metadata packed into UV2. */
  kraken:
    | {
        basePositions: Float32Array;
        deformedPositions: Float32Array;
        deformationUvs: Float32Array;
        normals: number[];
        indices: number[];
      }
    | null;
}

interface WreckTrace {
  mesh: BabylonMesh;
  debrisMesh: BabylonMesh;
  bornAt: number;
  centreX: number;
  centreZ: number;
  heading: number;
  localPositions: Float32Array;
  surfaceOffsets: Float32Array;
  positions: Float32Array;
  debrisLocalPositions: Float32Array;
  debrisSurfaceOffsets: Float32Array;
  debrisPositions: Float32Array;
  fullSizeAfter: number;
}

/**
 * True on a touch device — the same test the app shell uses to decide it is in touch
 * mode. Read once: a device does not change pointer type mid-watch, and re-querying per
 * instance would be a media-query evaluation inside the spawn path.
 */
const COARSE_POINTER = typeof window !== 'undefined'
  && (window.matchMedia?.('(pointer: coarse)').matches ?? false);

export class VesselViews {
  private readonly views = new Map<EntityId, VesselView>();
  private readonly prototypes: VesselPrototypes;
  private readonly wakeMaterial: PBRMetallicRoughnessMaterial;
  private readonly oilSlickMaterial: PBRMaterial;
  private readonly wreckDebrisMaterial: PBRMetallicRoughnessMaterial;
  private readonly krakenShadowMaterial: PBRMetallicRoughnessMaterial;
  private readonly krakenPressureMaterial: PBRMetallicRoughnessMaterial;
  private readonly surfaceMaterials: VesselMaterials;
  private readonly organicTextures: readonly [DynamicTexture, DynamicTexture, DynamicTexture];
  private readonly scene: Scene;
  private readonly wreckTraces = new Map<EntityId, WreckTrace>();

  /** Set from the environment each frame so foam matches the sea it sits on. */
  private foamColor = new Color3(0.86, 0.89, 0.92);

  constructor(
    scene: Scene,
    hullMaterial: PBRMetallicRoughnessMaterial,
    quality: 'ultra' | 'high' | 'medium' | 'low' = 'high',
  ) {
    this.scene = scene;

    const surface = (
      name: string,
      metallic: number,
      roughness: number,
      emissive?: Color3,
      mist: 'full' | 'distanceOnly' = 'full',
    ): PBRMetallicRoughnessMaterial => {
      const material = new PBRMetallicRoughnessMaterial(name, scene);
      material.baseColor = new Color3(1, 1, 1);
      material.metallic = metallic;
      material.roughness = roughness;
      material.emissiveColor = emissive ?? new Color3(0, 0, 0);
      material.maxSimultaneousLights = maxSimultaneousLightsFor(scene);
      new MistPlugin(material, mist === 'full' ? undefined : { nearField: false });
      return material;
    };

    /*
     * Flesh takes aerial perspective but not the platform's near field.
     *
     * It used to take neither. The dense sea-level mist turned the creature's wet black
     * chalk-white close in — a real defect, and the reason it was opted out — but opting
     * out of the whole model also took the distance fade with it, so a limb at two
     * kilometres stood in undimmed black while every hull beside it washed to grey.
     * Adam, on shift 10: "the same fade that makes ships' colour look more faded… that
     * should be the case with the tentacles as well." Splitting the two terms gives the
     * distance and leaves the chalk behind.
     */
    const organicMaterial = surface('vesselOrganic', 0.01, 0.78, undefined, 'distanceOnly');
    // A tiny blue-black floor preserves wet contour highlights in the finale's nearly
    // lightless storm. It is far below a luminous surface and only prevents the animal
    // from collapsing into the same value as the sky.
    organicMaterial.emissiveColor = new Color3(0.006, 0.010, 0.012);
    this.organicTextures = createOrganicSkinTextures(scene);
    organicMaterial.baseTexture = this.organicTextures[0];
    organicMaterial.normalTexture = this.organicTextures[1];
    organicMaterial.metallicRoughnessTexture = this.organicTextures[2];
    const eyeMaterial = surface(
      'vesselEye',
      0.01,
      0.38,
      new Color3(0.003, 0.0008, 0.0001),
      'distanceOnly',
    );
    eyeMaterial.baseColor = new Color3(0.42, 0.23, 0.04);
    const pupilMaterial = surface('vesselPupil', 0, 0.28, undefined, 'distanceOnly');
    pupilMaterial.baseColor = new Color3(0.04, 0.05, 0.055);

    this.surfaceMaterials = {
      paint: hullMaterial,
      deck: surface('vesselDeck', 0.05, 0.96),
      glass: surface('vesselGlass', 0.04, 0.16, new Color3(0.002, 0.004, 0.006)),
      metal: surface('vesselBareMetal', 0.48, 0.38),
      rubber: surface('vesselRubber', 0.01, 0.88),
      organic: organicMaterial,
      eye: eyeMaterial,
      pupil: pupilMaterial,
      navRed: surface('vesselNavRed', 0, 0.32, new Color3(4.0, 0.06, 0.025)),
      navGreen: surface('vesselNavGreen', 0, 0.32, new Color3(0.03, 3.2, 0.12)),
      lamp: surface('vesselLamp', 0, 0.28, new Color3(3.2, 2.5, 1.5)),
    };
    // Touch hardware gets a real geometry increase too, but not the desktop multiplier:
    // animated vertices cost power and heat as well as frame time on a phone.
    const closeDetail = COARSE_POINTER || quality === 'low' || quality === 'medium'
      ? 'mobile'
      : 'desktop';
    this.prototypes = buildVesselPrototypes(scene, this.surfaceMaterials, closeDetail);

    // Wake foam: unlit-ish white driven by vertex alpha, so a trail fades out along its
    // length without needing a texture or a second draw.
    this.wakeMaterial = new PBRMetallicRoughnessMaterial('wakeFoam', scene);
    this.wakeMaterial.baseColor = new Color3(0.9, 0.93, 0.96);
    this.wakeMaterial.metallic = 0;
    this.wakeMaterial.roughness = 1;
    this.wakeMaterial.emissiveColor = new Color3(0.06, 0.07, 0.08);
    this.wakeMaterial.transparencyMode = PBRMetallicRoughnessMaterial.PBRMATERIAL_ALPHABLEND;
    this.wakeMaterial.backFaceCulling = false;
    new MistPlugin(this.wakeMaterial);

    /*
     * A thin fuel film, not a black decal.
     *
     * Vertex colour supplies the broken petroleum-brown body and transparency. The mesh is
     * re-fitted to the authored wave field below, so the trace breaks across crests
     * instead of hovering as one flat plane.
     */
    this.oilSlickMaterial = new PBRMaterial('wreckOilSlick', scene);
    this.oilSlickMaterial.albedoColor = new Color3(1, 1, 1);
    this.oilSlickMaterial.emissiveColor = new Color3(0, 0, 0);
    // This mesh modulates an ocean that already contains the sky reflection. Lighting
    // the overlay as a second physical surface reflected the sky twice and erased the
    // dark film; unlit here means "tint the rendered water", not "glow".
    this.oilSlickMaterial.unlit = true;
    this.oilSlickMaterial.transparencyMode =
      PBRMetallicRoughnessMaterial.PBRMATERIAL_ALPHABLEND;
    this.oilSlickMaterial.backFaceCulling = false;
    this.oilSlickMaterial.disableDepthWrite = true;
    /*
     * The water underneath has already received the full atmosphere. Fogging this
     * transparent modulation again bleaches it toward the horizon colour and erases it,
     * the same double-fog failure the submerged Kraken shadow had.
     */
    this.oilSlickMaterial.fogEnabled = false;

    // A separate opaque pass makes torn structure read as solid wreckage instead of
    // being alpha-sorted into the fuel film. Every loss shares this material.
    this.wreckDebrisMaterial = new PBRMetallicRoughnessMaterial('wreckDebris', scene);
    this.wreckDebrisMaterial.baseColor = new Color3(1, 1, 1);
    this.wreckDebrisMaterial.metallic = 0.08;
    this.wreckDebrisMaterial.roughness = 0.88;
    this.wreckDebrisMaterial.emissiveColor = new Color3(0.008, 0.009, 0.008);
    this.wreckDebrisMaterial.backFaceCulling = false;
    new MistPlugin(this.wreckDebrisMaterial);

    // This is not an illuminated underwater model. It is a dark, translucent stain on
    // the moving surface, with all softness supplied by vertex alpha and broad geometry.
    this.krakenShadowMaterial = new PBRMetallicRoughnessMaterial('krakenSubmergedMass', scene);
    this.krakenShadowMaterial.baseColor = new Color3(0.006, 0.016, 0.023);
    this.krakenShadowMaterial.emissiveColor = new Color3(0, 0.001, 0.002);
    this.krakenShadowMaterial.metallic = 0;
    this.krakenShadowMaterial.roughness = 0.94;
    this.krakenShadowMaterial.transparencyMode =
      PBRMetallicRoughnessMaterial.PBRMATERIAL_ALPHABLEND;
    this.krakenShadowMaterial.backFaceCulling = false;
    this.krakenShadowMaterial.disableDepthWrite = true;
    this.krakenShadowMaterial.fogEnabled = false;
    // The water below has already been fogged. Fogging this transparent darkening layer
    // again erases it completely by 4 km; leaving it un-fogged preserves a faint stain
    // without making an illuminated object appear through the haze.

    // The same double-fog problem erased the one-pixel pressure line at 10 km. This
    // separate low-emission foam material remains faint but lets that first disturbance
    // survive against an already fogged horizon.
    this.krakenPressureMaterial = new PBRMetallicRoughnessMaterial(
      'krakenPressureFoam',
      scene,
    );
    this.krakenPressureMaterial.baseColor = new Color3(0.62, 0.67, 0.72);
    // Aerated water remains a pale value even in the night storm. A modest linear
    // emission floor keeps the thin contact ribbons from disappearing after the ocean
    // beneath them has already been lit and graded.
    this.krakenPressureMaterial.emissiveColor = new Color3(0.28, 0.32, 0.36);
    this.krakenPressureMaterial.metallic = 0;
    this.krakenPressureMaterial.roughness = 1;
    this.krakenPressureMaterial.transparencyMode =
      PBRMetallicRoughnessMaterial.PBRMATERIAL_ALPHABLEND;
    this.krakenPressureMaterial.backFaceCulling = false;
    this.krakenPressureMaterial.disableDepthWrite = true;
    this.krakenPressureMaterial.fogEnabled = false;
  }

  get triangleCount(): number {
    for (const view of this.views.values()) {
      if (view.closeGeometry) return this.prototypes.closeTriangleCount;
    }
    return this.prototypes.farTriangleCount;
  }

  setFoamColor(c: { r: number; g: number; b: number }): void {
    this.foamColor.set(c.r, c.g, c.b);
    this.wakeMaterial.baseColor.set(c.r, c.g, c.b);
    this.krakenPressureMaterial.baseColor.set(c.r, c.g, c.b);
  }

  /** Remove wreck traces at the shift boundary; within a shift they deliberately persist. */
  clearWreckTraces(): void {
    for (const trace of this.wreckTraces.values()) {
      trace.mesh.dispose();
      trace.debrisMesh.dispose();
    }
    this.wreckTraces.clear();
  }

  /**
   * Swap only the creature's geometry at the close-range boundary. Simulation identity,
   * attack state and transforms stay untouched; the next sync below deforms the new
   * vertices from the same deterministic clock.
   */
  private replaceCreatureGeometry(
    view: VesselView,
    vessel: Vessel,
    useClose: boolean,
  ): void {
    const proto = useClose
      ? this.prototypes.closeMeshes.get(vessel.kind)
      : this.prototypes.meshes.get(vessel.kind);
    if (!proto) return;

    const clone = proto.clone(
      `vessel_${vessel.kind}_${vessel.id}_${useClose ? 'close' : 'far'}`,
      null,
      false,
    );
    if (!clone) return;
    clone.makeGeometryUnique();
    const positions = clone.getVerticesData(VertexBuffer.PositionKind);
    const deformationUvs = clone.getVerticesData(VertexBuffer.UV2Kind);
    const indices = clone.getIndices();
    if (!positions || !deformationUvs || !indices) {
      clone.dispose();
      return;
    }

    const deformationState = {
      basePositions: Float32Array.from(positions),
      deformedPositions: Float32Array.from(positions),
      deformationUvs: Float32Array.from(deformationUvs),
      normals: new Array(positions.length).fill(0),
      indices: Array.from(indices),
    };
    const old = view.instance;
    view.instance = clone;
    view.tentacle = vessel.kind === 'tentacle' ? deformationState : null;
    view.kraken = vessel.kind === 'kraken' ? deformationState : null;
    view.closeGeometry = useClose;
    clone.setEnabled(true);
    clone.alwaysSelectAsActiveMesh = true;
    clone.isPickable = false;
    old.dispose();
  }

  /**
   * Reconcile the instance set with the simulation's vessel list, then push transforms.
   * `surfaceAt` samples the same analytic wave field the sim uses, so wake ribbons ride
   * the water rather than floating over it.
   */
  sync(
    vessels: readonly Vessel[],
    dt: number,
    surfaceAt: (x: number, z: number) => number,
    simTime: number,
    /** The finale's phase machine, when one is running — drives the rise and the fall. */
    kraken: { phase: string; timer: number } | null = null,
  ): void {
    const seen = new Set<EntityId>();
    const predation = new Map<EntityId, KrakenPredationPose>();
    for (const vessel of vessels) {
      const pose = krakenPredationPose(vessel, vessels, simTime);
      if (pose) predation.set(vessel.id, pose);
    }

    for (const v of vessels) {
      seen.add(v.id);
      if (v.state === 'sinking') this.ensureWreckTrace(v, simTime);
      let view = this.views.get(v.id);

      if (!view) {
        const creature = v.kind === 'tentacle' || v.kind === 'kraken';
        const closeGeometry = creature && Math.hypot(v.pos.x, v.pos.z) < 1500;
        const proto = closeGeometry
          ? this.prototypes.closeMeshes.get(v.kind)
          : this.prototypes.meshes.get(v.kind);
        if (!proto) continue;
        let instance: InstancedMesh | BabylonMesh;
        let tentacle: VesselView['tentacle'] = null;
        let kraken: VesselView['kraken'] = null;

        if (v.kind === 'tentacle' || v.kind === 'kraken') {
          const clone = proto.clone(`vessel_${v.kind}_${v.id}`, null, false);
          if (!clone) continue;
          clone.makeGeometryUnique();
          clone.setEnabled(true);
          instance = clone;

          const base = clone.getVerticesData(VertexBuffer.PositionKind);
          const indices = clone.getIndices();
          if (base && indices && v.kind === 'tentacle') {
            const deformationUvs = clone.getVerticesData(VertexBuffer.UV2Kind);
            if (deformationUvs) {
              tentacle = {
                basePositions: Float32Array.from(base),
                deformedPositions: Float32Array.from(base),
                deformationUvs: Float32Array.from(deformationUvs),
                normals: new Array(base.length).fill(0),
                indices: Array.from(indices),
              };
            }
          } else if (base && indices && v.kind === 'kraken') {
            const deformationUvs = clone.getVerticesData(VertexBuffer.UV2Kind);
            if (deformationUvs) {
              kraken = {
                basePositions: Float32Array.from(base),
                deformedPositions: Float32Array.from(base),
                deformationUvs: Float32Array.from(deformationUvs),
                normals: new Array(base.length).fill(0),
                indices: Array.from(indices),
              };
            }
          }
        } else {
          instance = proto.createInstance(`vessel_${v.kind}_${v.id}`);
        }

        view = {
          instance,
          kind: v.kind,
          closeGeometry,
          wake: new Float32Array(WAKE_POINTS * 3),
          wakeCount: 0,
          wakeHead: 0,
          wakeTimer: 0,
          wakeMesh: null,
          contactMesh: null,
          submergedMesh: null,
          tentacle,
          kraken,
        };
        view.instance.alwaysSelectAsActiveMesh = true;
        view.instance.isPickable = false;
        this.views.set(v.id, view);
        this.primeWake(view, v);
      }

      if (v.kind === 'tentacle' || v.kind === 'kraken') {
        const range = Math.hypot(v.pos.x, v.pos.z);
        // Forty metres of hysteresis straddles the 1.5 km boundary. A creature moving
        // through a swell cannot alternate meshes frame-by-frame at the threshold.
        const wantsClose = view.closeGeometry ? range < 1520 : range < 1480;
        if (wantsClose !== view.closeGeometry) {
          this.replaceCreatureGeometry(view, v, wantsClose);
        }
      }

      // The simulation owns attitude completely, including the list and settle of a
      // sinking hull, so the view never second-guesses it.
      const inst = view.instance;
      inst.position.set(v.pos.x, v.pos.y, v.pos.z);
      inst.rotationQuaternion = null;
      inst.rotation.set(v.pitch, v.heading, v.roll);
      inst.scaling.setAll(1);
      const predationPose = predation.get(v.id);
      if (predationPose) {
        inst.position.x += predationPose.offsetX;
        inst.position.y += predationPose.offsetY;
        inst.position.z += predationPose.offsetZ;
        // An explicit quaternion makes the list local to the already-yawed hull. Adding
        // Euler Z after yaw rotated around the scene instead, which left a broadside
        // tanker looking inexplicably level in the first contact captures.
        const yawAndPitch = Quaternion.RotationYawPitchRoll(
          v.heading,
          v.pitch + predationPose.pitch,
          0,
        );
        const localRoll = Quaternion.RotationAxis(
          new Vector3(0, 0, 1),
          v.roll + predationPose.roll,
        );
        inst.rotationQuaternion = yawAndPitch.multiply(localRoll);
        inst.scaling.set(
          1 + predationPose.crush * 0.035,
          1 - predationPose.crush * 0.10,
          1 - predationPose.crush * 0.025,
        );
      }
      const presence = v.kind === 'kraken'
        ? krakenPresence(Math.hypot(v.pos.x, v.pos.z), v.submergence)
        : null;
      if (v.kind === 'kraken') {
        // The surfaced mass moves on a slow breathing rhythm. Scaling from the waterline
        // rather than rocking the whole body avoids turning a colossal animal into a
        // buoy, and sim time keeps capture/replay deterministic.
        const breath = Math.sin(simTime * 0.22 + v.id * 0.37);
        inst.scaling.set(1 + breath * 0.012, 1 + breath * 0.035, 1 - breath * 0.008);
        inst.position.y += breath * 1.8;
        inst.visibility = presence?.bodyVisibility ?? 1;

        /*
         * The dive itself: the body goes down through the water and comes back up
         * through it. `submergence` is ramped by the phase machine over five and a half
         * seconds, so this is a mass descending rather than a mesh being switched off.
         *
         * Skipped once the creature is dying, because the fall already owns its vertical
         * motion — adding the dive on top would drop it twice as far as either intends.
         */
        const dying = kraken?.phase === 'dying' || kraken?.phase === 'gone';
        if (!dying && v.submergence > 0) {
          inst.position.y -= v.submergence * KRAKEN.diveDepth;
        }

        /*
         * The finale's rise and fall — the head pitching back out of the sea until the
         * eye clears the deck line, and after the killing burst, the fall away and under.
         *
         * The pitch/lift pair comes from `content/creatureScale.ts`, the same contract
         * the simulation derives the eye's aim point from, so the player is shooting
         * exactly the eye they are looking into. The sign is negative here because
         * Babylon's positive X rotation tips the bow down; the contract's angle lifts it.
         */
        if (kraken && (kraken.phase === 'rise' || kraken.phase === 'dying' || kraken.phase === 'gone')) {
          let pitch: number;
          let lift: number;
          if (kraken.phase === 'rise') {
            const p = Math.min(1, kraken.timer / KRAKEN.riseSeconds);
            const pose = krakenRisePose(p);
            pitch = pose.pitch;
            lift = pose.lift;
          } else if (kraken.phase === 'gone') {
            // Hold the end of the fall — without this the mesh snapped back to its
            // ordinary sinking pose on the phase change, at the exact moment the sea
            // is supposed to have closed over it.
            pitch = -0.42;
            lift = -130;
          } else {
            const rollEnd = KRAKEN.eyeRollSeconds;
            if (kraken.timer < rollEnd) {
              // The eye rolls back while the head holds its full rise.
              const pose = krakenRisePose(1);
              pitch = pose.pitch;
              lift = pose.lift;
            } else {
              // The fall: the head keeps going past vertical the other way while the
              // whole mass slides under. Ease-in — dead weight, not a spring.
              const f = Math.min(1, (kraken.timer - rollEnd) / KRAKEN.fallSeconds);
              const heavy = f * f;
              const pose = krakenRisePose(1);
              pitch = pose.pitch - (pose.pitch + 0.42) * heavy;
              lift = pose.lift - (pose.lift + 130) * heavy;
            }
          }
          inst.rotation.x -= pitch;
          inst.position.y += lift;
        }
      }

      const tentacleTarget = view.tentacle ? krakenTentacleTarget(v, vessels) : null;
      const tentacleGrip = tentacleTarget
        ? krakenTentacleGrip(v, tentacleTarget, simTime)
        : null;
      const rigGrip = view.tentacle && !tentacleTarget
        ? krakenRigGrip(v, simTime)
        : null;
      if (view.tentacle) this.animateTentacle(view, v, vessels, simTime);
      // Avoid deforming nearly 600k triangles while the creature is still represented
      // only by its pressure shadow at long range.
      if (view.kraken && (presence?.bodyVisibility ?? 1) > 0.01) {
        // The stare holds the eye open; the kill rolls it back into the head.
        let eyeState: KrakenEyeState | undefined;
        if (kraken?.phase === 'rise') {
          eyeState = { hold: Math.min(1, kraken.timer / KRAKEN.riseSeconds), rollBack: 0 };
        } else if (kraken?.phase === 'dying') {
          eyeState = { hold: 1, rollBack: Math.min(1, kraken.timer / KRAKEN.eyeRollSeconds) };
        }
        this.animateKraken(view, v, simTime, eyeState);
      }
      if (v.kind === 'kraken' && presence) {
        this.updateKrakenShadow(view, v, surfaceAt, simTime, presence, kraken);
      }
      this.updateWake(view, v, dt, surfaceAt);
      this.updateContactFoam(
        view,
        v,
        surfaceAt,
        simTime,
        predationPose,
        tentacleGrip,
        rigGrip,
      );
    }

    this.updateWreckTraces(surfaceAt, simTime);

    // Drop views the sim no longer has.
    for (const [id, view] of this.views) {
      if (seen.has(id)) continue;
      view.instance.dispose();
      view.wakeMesh?.dispose();
      view.contactMesh?.dispose();
      view.submergedMesh?.dispose();
      this.views.delete(id);
    }
  }

  /**
   * Leave a growing oil trace where a conventional hull was lost.
   *
   * Creatures do not leak fuel and a submerged mini-sub should not paint the surface the
   * instant it dies. Everything else in the roster carries enough fuel or machinery oil
   * to mark the water.
   */
  private ensureWreckTrace(v: Vessel, simTime: number): void {
    if (
      this.wreckTraces.has(v.id)
      || v.kind === 'tentacle'
      || v.kind === 'kraken'
      || v.kind === 'minisub'
    ) {
      return;
    }

    if (this.wreckTraces.size >= WRECK_TRACE_LIMIT) {
      const oldest = this.wreckTraces.keys().next().value as EntityId | undefined;
      if (oldest !== undefined) {
        const retired = this.wreckTraces.get(oldest);
        retired?.mesh.dispose();
        retired?.debrisMesh.dispose();
        this.wreckTraces.delete(oldest);
      }
    }

    const spec = VESSEL_SPECS[v.kind];
    const longRadius = Math.max(18, Math.min(70, spec.length * 0.38));
    const wideRadius = Math.max(
      12,
      Math.min(45, spec.beam * 1.50 + spec.length * 0.070),
    );
    const local: number[] = [];
    const surfaceOffsets: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const debrisLocal: number[] = [];
    const debrisSurfaceOffsets: number[] = [];
    const debrisColors: number[] = [];
    const debrisIndices: number[] = [];
    const phase = v.id * 1.618;

    const addLobe = (
      forwardOffset: number,
      rightOffset: number,
      forwardRadius: number,
      rightRadius: number,
      tint: readonly [number, number, number],
      alpha: number,
      phaseOffset: number,
    ): void => {
      const centre = local.length / 2;
      local.push(forwardOffset, rightOffset);
      surfaceOffsets.push(0.14);
      colors.push(tint[0] * 0.72, tint[1] * 0.72, tint[2] * 0.72, alpha);

      const inner = local.length / 2;
      for (let i = 0; i < SLICK_SEGMENTS; i++) {
        const angle = (i / SLICK_SEGMENTS) * Math.PI * 2;
        const ragged =
          1
          + Math.sin(angle * 3 + phase + phaseOffset) * 0.13
          + Math.sin(angle * 7 - phase * 0.63 + phaseOffset) * 0.055;
        local.push(
          forwardOffset + Math.cos(angle) * forwardRadius * 0.72 * ragged,
          rightOffset + Math.sin(angle) * rightRadius * 0.72 * ragged,
        );
        surfaceOffsets.push(0.14);
        colors.push(tint[0], tint[1], tint[2], alpha);
      }

      const outer = local.length / 2;
      for (let i = 0; i < SLICK_SEGMENTS; i++) {
        const angle = (i / SLICK_SEGMENTS) * Math.PI * 2;
        const ragged =
          1
          + Math.sin(angle * 3 + phase + phaseOffset) * 0.18
          + Math.sin(angle * 7 - phase * 0.63 + phaseOffset) * 0.075;
        local.push(
          forwardOffset + Math.cos(angle) * forwardRadius * ragged,
          rightOffset + Math.sin(angle) * rightRadius * ragged,
        );
        surfaceOffsets.push(0.14);
        colors.push(tint[0] * 1.18, tint[1] * 1.18, tint[2] * 1.18, 0);
      }

      for (let i = 0; i < SLICK_SEGMENTS; i++) {
        const next = (i + 1) % SLICK_SEGMENTS;
        indices.push(
          centre, inner + i, inner + next,
          inner + i, outer + i, inner + next,
          inner + next, outer + i, outer + next,
        );
      }
    };

    addLobe(
      0,
      0,
      longRadius,
      wideRadius,
      [0.028, 0.010, 0.002],
      SLICK_OPACITY,
      0,
    );
    addLobe(
      -longRadius * 0.20,
      wideRadius * 0.38,
      longRadius * 0.62,
      wideRadius * 0.66,
      [0.052, 0.017, 0.002],
      SLICK_OPACITY,
      2.1,
    );

    /*
     * A few identifiable fragments turn the trace from "dark patch" into wreckage.
     *
     * One small opaque batch per wreck lets torn edges catch light instead of being
     * alpha-sorted into the fuel film. Positions, sizes and colours derive only from
     * entity id and hull dimensions, so the same loss reconstructs exactly in replay.
     */
    const debrisCount = Math.max(3, Math.min(6, Math.round(spec.length / 45) + 2));
    for (let piece = 0; piece < debrisCount; piece++) {
      const seed = phase + piece * 2.731;
      const spreadAngle = phase * 0.71 + (piece / debrisCount) * Math.PI * 2;
      const centreForward = Math.cos(spreadAngle) * longRadius
        * (0.72 + 0.08 * Math.sin(seed * 1.37));
      const centreRight = Math.sin(spreadAngle) * wideRadius
        * (0.82 + 0.08 * Math.cos(seed * 1.19));
      const angle = seed * 0.83 + Math.sin(seed * 2.17) * 0.45;
      const pieceLength = Math.min(5.4, Math.max(2.1, spec.length * 0.026))
        * (0.78 + 0.22 * (0.5 + 0.5 * Math.sin(seed * 2.43)));
      const pieceWidth = pieceLength * (0.28 + 0.16 * (0.5 + 0.5 * Math.cos(seed * 1.71)));
      const alongX = Math.cos(angle) * pieceLength * 0.5;
      const alongY = Math.sin(angle) * pieceLength * 0.5;
      const acrossX = -Math.sin(angle) * pieceWidth * 0.5;
      const acrossY = Math.cos(angle) * pieceWidth * 0.5;
      const base = debrisLocal.length / 2;
      const corners = [
        [-alongX - acrossX * 0.78, -alongY - acrossY * 0.78],
        [alongX - acrossX * 0.55, alongY - acrossY * 0.55],
        [alongX + acrossX, alongY + acrossY],
        [-alongX + acrossX * 0.72, -alongY + acrossY * 0.72],
      ] as const;
      const tint = piece % 3 === 0
        ? [0.270, 0.085, 0.012] as const
        : [0.180, 0.195, 0.178] as const;

      for (let corner = 0; corner < corners.length; corner++) {
        const point = corners[corner] as readonly [number, number];
        debrisLocal.push(centreForward + point[0], centreRight + point[1]);
        // One torn edge catches above the surface. A perfectly flat plate vanishes at
        // the player's low inspection angle and reads as another patch of oil.
        const tornEdge = [0.24, 0.31, 0.82, 0.61] as const;
        debrisSurfaceOffsets.push(
          (tornEdge[(corner + piece) % tornEdge.length] as number)
          + (piece % 2) * 0.035,
        );
        const edgeShade = 0.82 + corner * 0.055;
        debrisColors.push(
          tint[0] * edgeShade,
          tint[1] * edgeShade,
          tint[2] * edgeShade,
          1,
        );
      }
      debrisIndices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }

    const mesh = new BabylonMesh(`wreckOil_${v.id}`, this.scene);
    mesh.material = this.oilSlickMaterial;
    mesh.position.set(v.pos.x, 0, v.pos.z);
    mesh.isPickable = false;
    mesh.alwaysSelectAsActiveMesh = true;
    mesh.useVertexColors = true;
    mesh.hasVertexAlpha = true;

    const positions = new Float32Array((local.length / 2) * 3);
    const vd = new VertexData();
    vd.positions = Array.from(positions);
    vd.indices = indices;
    vd.colors = colors;
    vd.normals = new Array(positions.length).fill(0).map((_, i) => (i % 3 === 1 ? 1 : 0));
    vd.applyToMesh(mesh, true);

    const debrisMesh = new BabylonMesh(`wreckDebris_${v.id}`, this.scene);
    debrisMesh.material = this.wreckDebrisMaterial;
    debrisMesh.position.set(v.pos.x, 0, v.pos.z);
    debrisMesh.isPickable = false;
    debrisMesh.alwaysSelectAsActiveMesh = true;
    debrisMesh.useVertexColors = true;
    const debrisPositions = new Float32Array((debrisLocal.length / 2) * 3);
    const debrisVd = new VertexData();
    debrisVd.positions = Array.from(debrisPositions);
    debrisVd.indices = debrisIndices;
    debrisVd.colors = debrisColors;
    debrisVd.normals = new Array(debrisPositions.length)
      .fill(0)
      .map((_, i) => (i % 3 === 1 ? 1 : 0));
    debrisVd.applyToMesh(debrisMesh, true);

    this.wreckTraces.set(v.id, {
      mesh,
      debrisMesh,
      bornAt: simTime - Math.max(0, v.stateTimer),
      centreX: v.pos.x,
      centreZ: v.pos.z,
      heading: v.heading,
      localPositions: Float32Array.from(local),
      surfaceOffsets: Float32Array.from(surfaceOffsets),
      positions,
      debrisLocalPositions: Float32Array.from(debrisLocal),
      debrisSurfaceOffsets: Float32Array.from(debrisSurfaceOffsets),
      debrisPositions,
      fullSizeAfter: 10 + Math.min(28, spec.length * 0.10),
    });
  }

  /** Fit every persistent trace to the same moving surface the ships and wakes use. */
  private updateWreckTraces(
    surfaceAt: (x: number, z: number) => number,
    simTime: number,
  ): void {
    for (const [id, trace] of this.wreckTraces) {
      const age = Math.max(0, simTime - trace.bornAt);

      // Spent: the oil has dispersed. Meshes go back to the driver rather than lingering
      // as invisible geometry, which also keeps the WRECK_TRACE_LIMIT slots free for
      // whatever the player is fighting now.
      if (age >= SLICK_LIFETIME) {
        trace.mesh.dispose();
        trace.debrisMesh.dispose();
        this.wreckTraces.delete(id);
        continue;
      }

      // Thinning out over the last stretch. `visibility` multiplies the material's alpha
      // per mesh, which is the only per-trace fade available — the slick material is one
      // shared instance across every wreck on the water.
      const fade = Math.min(1, (SLICK_LIFETIME - age) / SLICK_FADE);
      trace.mesh.visibility = fade;
      trace.debrisMesh.visibility = fade;

      const growth = 0.16 + 0.84 * (1 - Math.exp(-age * 3 / trace.fullSizeAfter));
      const fx = Math.sin(trace.heading);
      const fz = Math.cos(trace.heading);
      const rx = Math.cos(trace.heading);
      const rz = -Math.sin(trace.heading);

      for (
        let localIndex = 0, positionIndex = 0;
        localIndex < trace.localPositions.length;
        localIndex += 2, positionIndex += 3
      ) {
        const forward = (trace.localPositions[localIndex] as number) * growth;
        const right = (trace.localPositions[localIndex + 1] as number) * growth;
        const offsetX = fx * forward + rx * right;
        const offsetZ = fz * forward + rz * right;
        const x = trace.centreX + offsetX;
        const z = trace.centreZ + offsetZ;
        trace.positions[positionIndex] = offsetX;
        trace.positions[positionIndex + 1] =
          surfaceAt(x, z) + (trace.surfaceOffsets[localIndex / 2] as number);
        trace.positions[positionIndex + 2] = offsetZ;
      }

      trace.mesh.updateVerticesData(
        VertexBuffer.PositionKind,
        trace.positions,
        false,
        false,
      );

      for (
        let localIndex = 0, positionIndex = 0;
        localIndex < trace.debrisLocalPositions.length;
        localIndex += 2, positionIndex += 3
      ) {
        const forward = (trace.debrisLocalPositions[localIndex] as number) * growth;
        const right = (trace.debrisLocalPositions[localIndex + 1] as number) * growth;
        const offsetX = fx * forward + rx * right;
        const offsetZ = fz * forward + rz * right;
        const x = trace.centreX + offsetX;
        const z = trace.centreZ + offsetZ;
        trace.debrisPositions[positionIndex] = offsetX;
        trace.debrisPositions[positionIndex + 1] =
          surfaceAt(x, z) + (trace.debrisSurfaceOffsets[localIndex / 2] as number);
        trace.debrisPositions[positionIndex + 2] = offsetZ;
      }

      trace.debrisMesh.updateVerticesData(
        VertexBuffer.PositionKind,
        trace.debrisPositions,
        false,
        false,
      );
    }
  }

  /**
   * Vessels enter the playable area after steaming for kilometres, so a new render view
   * should not begin with a physically impossible clean stern. Priming a short,
   * deterministic history also makes frozen benchmark frames judge the wake instead of
   * an artifact of the capture harness being paused.
   */
  private primeWake(view: VesselView, v: Vessel): void {
    if (v.speed <= 1.2 || v.kind === 'tentacle') return;
    const spec = VESSEL_SPECS[v.kind];
    const count = Math.min(WAKE_POINTS, 42);
    const fx = Math.sin(v.heading);
    const fz = Math.cos(v.heading);
    const rx = Math.cos(v.heading);
    const rz = -Math.sin(v.heading);
    const sternX = v.pos.x - fx * spec.length * 0.45;
    const sternZ = v.pos.z - fz * spec.length * 0.45;

    for (let k = 0; k < count; k++) {
      const age = (count - 1 - k) * WAKE_INTERVAL;
      const distance = v.speed * age;
      const meander = Math.sin(v.id * 1.73 + age * 0.34) * spec.beam * 0.035 * age;
      const i = k * 3;
      view.wake[i] = sternX - fx * distance + rx * meander;
      view.wake[i + 1] = v.heading;
      view.wake[i + 2] = sternZ - fz * distance + rz * meander;
    }
    view.wakeCount = count;
    view.wakeHead = count % WAKE_POINTS;
  }

  /**
   * Bend every height slice by a different amount. A rigid rotation makes a post sway;
   * this height-dependent deformation makes a limb writhe, with the planted base moving
   * least and the hook moving most. It is driven only by sim time and entity id, so
   * captures and replays remain deterministic.
   */
  private animateTentacle(
    view: VesselView,
    v: Vessel,
    vessels: readonly Vessel[],
    simTime: number,
  ): void {
    const state = view.tentacle;
    if (!state) return;

    /*
     * The centreline is not computed here. `content/tentaclePose.ts` owns the deformed
     * curve — emergence, travelling wave and grip wrap — because the simulation strings
     * the limb's hit spheres along the identical call. This function's job is only what
     * the gun does not care about: sweeping the tube's skin around that curve, the axial
     * roll, and the muscle pulse.
     */
    const pose = tentaclePose(v, vessels, simTime);
    const motion = pose.motion;
    const rise = pose.rise;
    const gripStrength = pose.gripStrength;
    const clutch = pose.shipGrip?.clutch ?? pose.rigGrip?.clutch ?? 1;
    const phase = v.id * 1.618;
    const base = state.basePositions;
    const out = state.deformedPositions;
    const deformation = state.deformationUvs;
    const frameCache = new Map<number, {
      baseCentre: Vector3;
      centre: Vector3;
      baseSide: Vector3;
      baseAcross: Vector3;
      baseTangent: Vector3;
      side: Vector3;
      across: Vector3;
      tangent: Vector3;
      cosTwist: number;
      sinTwist: number;
    }>();
    const reference = new Vector3(0, 0, 1);
    const fallback = new Vector3(1, 0, 0);

    const centreAt = (progress: number): Vector3 => {
      const p = tentacleCentrePoint(pose, progress, simTime);
      return new Vector3(p.x, p.y, p.z);
    };

    const frameAt = (t: number) => {
      const cached = frameCache.get(t);
      if (cached) return cached;
      const epsilon = 0.008;
      const before = Math.max(0, t - epsilon);
      const after = Math.min(1, t + epsilon);
      const baseCentre = krakenTentaclePathPoint(t);
      const baseTangent = krakenTentaclePathPoint(after)
        .subtract(krakenTentaclePathPoint(before))
        .normalize();
      let baseSide = Vector3.Cross(baseTangent, reference);
      if (baseSide.lengthSquared() < 1e-5) baseSide = Vector3.Cross(baseTangent, fallback);
      baseSide.normalize();
      const baseAcross = Vector3.Cross(baseTangent, baseSide).normalize();

      const centre = centreAt(t);
      const tangent = centreAt(after).subtract(centreAt(before)).normalize();
      let side = Vector3.Cross(tangent, reference);
      if (side.lengthSquared() < 1e-5) side = Vector3.Cross(tangent, fallback);
      side.normalize();
      const across = Vector3.Cross(tangent, side).normalize();
      // The roll depends only on progress, and every vertex on a ring shares it — so it
      // belongs in the per-progress cache, not in the per-vertex loop.
      const twist = tentacleRoll(motion, t, simTime) * rise;
      const frame = {
        baseCentre,
        centre,
        baseSide,
        baseAcross,
        baseTangent,
        side,
        across,
        tangent,
        cosTwist: Math.cos(twist),
        sinTwist: Math.sin(twist),
      };
      frameCache.set(t, frame);
      return frame;
    };

    for (let i = 0, vertex = 0; i < base.length; i += 3, vertex++) {
      const x = base[i] as number;
      const y = base[i + 1] as number;
      const z = base[i + 2] as number;
      const t = deformation[vertex * 2 + 1] as number;
      const frame = frameAt(t);
      const fine = Math.pow(t, 2.2);
      const offset = new Vector3(
        x - frame.baseCentre.x,
        y - frame.baseCentre.y,
        z - frame.baseCentre.z,
      );
      const sideAmount = Vector3.Dot(offset, frame.baseSide);
      const acrossAmount = Vector3.Dot(offset, frame.baseAcross);
      const tangentAmount = Vector3.Dot(offset, frame.baseTangent);
      const contraction = gripStrength
        * Math.sin(Math.PI * Math.max(0, Math.min(1, (t - 0.24) / 0.72)))
        * 0.11
        * clutch;
      const pulse = 1
        + Math.sin(simTime * 1.15 + phase + t * 7.0) * 0.045 * fine
        + contraction;
      /*
       * Roll about the limb's own axis.
       *
       * The single cheapest signal that a thing is alive: the mesh already carries two
       * rows of suckers down the inside of the hook, and turning them into and out of
       * view as the wave passes reads as muscle twisting under skin. It is a rotation of
       * the local frame that already exists, so it costs one sine and two multiplies, and
       * it never moves the centreline — the hit volume is indifferent to it.
       */
      const { cosTwist, sinTwist } = frame;
      const rolledSide = sideAmount * cosTwist - acrossAmount * sinTwist;
      const rolledAcross = sideAmount * sinTwist + acrossAmount * cosTwist;
      const transformed = frame.side.scale(rolledSide * pulse)
        .add(frame.across.scale(rolledAcross * pulse))
        .add(frame.tangent.scale(tangentAmount));

      out[i] = frame.centre.x + transformed.x;
      out[i + 1] = frame.centre.y + transformed.y;
      out[i + 2] = frame.centre.z + transformed.z;
    }

    VertexData.ComputeNormals(out, state.indices, state.normals);
    view.instance.updateVerticesData(VertexBuffer.PositionKind, out, true, false);
    view.instance.updateVerticesData(VertexBuffer.NormalKind, state.normals, true, false);
  }

  /**
   * Move the eight authored Kraken arms as muscles, not hinges.
   *
   * Every lengthwise ring receives one translation from a travelling contraction. The
   * planted root is still, the middle carries the slow heavy wave, and the tip follows
   * late with a tighter curl. Sucker primitives share the same arm/progress metadata, so
   * they remain embedded in the moving underside rather than sliding over it.
   */
  private animateKraken(
    view: VesselView,
    v: Vessel,
    simTime: number,
    eye?: KrakenEyeState,
  ): void {
    const state = view.kraken;
    if (!state) return;

    const base = state.basePositions;
    const out = state.deformedPositions;
    const deformation = state.deformationUvs;
    const attacking = v.state === 'attacking';

    for (let i = 0, vertex = 0; i < base.length; i += 3, vertex++) {
      const armGroup = Math.round(deformation[vertex * 2] as number);
      if (armGroup <= 0) {
        out[i] = base[i] as number;
        out[i + 1] = base[i + 1] as number;
        out[i + 2] = base[i + 2] as number;
        continue;
      }

      const t = deformation[vertex * 2 + 1] as number;
      if (armGroup === 9) {
        const offset = krakenEyeOffset(Math.round(t), simTime, eye);
        out[i] = (base[i] as number) + offset.x;
        out[i + 1] = (base[i + 1] as number) + offset.y;
        out[i + 2] = (base[i + 2] as number) + offset.z;
        continue;
      }
      if (armGroup === 10) {
        // The maw stays well below the ordinary water crop. Predation raises it for a
        // short, breathing reveal; it is never a permanent face on the mantle.
        const revealY = attacking
          ? 104 + Math.sin(simTime * 0.82 + t * 1.7) * 7
          : -42;
        out[i] = (base[i] as number) + Math.sin(simTime * 0.41) * (attacking ? 4 : 0);
        out[i + 1] = (base[i + 1] as number) + revealY;
        out[i + 2] = (base[i + 2] as number) + (attacking ? 18 : -16);
        continue;
      }
      const offset = krakenArmOffset(armGroup - 1, t, simTime, attacking);
      out[i] = (base[i] as number) + offset.x;
      out[i + 1] = (base[i + 1] as number) + offset.y;
      out[i + 2] = (base[i + 2] as number) + offset.z;
    }

    VertexData.ComputeNormals(out, state.indices, state.normals);
    // alwaysSelectAsActiveMesh keeps the boss from being culled, so recalculating the
    // enormous bounding box every frame would be pure cost.
    view.instance.updateVerticesData(VertexBuffer.PositionKind, out, false, false);
    view.instance.updateVerticesData(VertexBuffer.NormalKind, state.normals, false, false);
  }

  /**
   * The dark mass under the surface — the one legible trace of a submerged Kraken.
   *
   * This is the survivor of the painted-proxy purge. The creature used to be drawn as a
   * pale multi-ring overlay — soft concentric lobes, arm ribbons, an animated
   * refraction rim — which is what Adam was looking at when he said "remove ALL these
   * rings, no matter the size". Deleting it wholesale went one step too far: the real
   * mesh dives 300 m behind an opaque ocean, so the dread phase — whose script promises
   * "the shape the renderer draws under the water" — showed nothing at all, and Adam
   * sent that straight back.
   *
   * What returns is only the close treatment the purge was protecting: a glassy stretch
   * of sea a step darker than the rest, soft-edged and slightly domed, sliding under
   * the storm. Everything that made the old overlay read as rings or pack ice stays
   * dead — no pale mode, no discrete alpha bands (the falloff is one smooth curve), no
   * per-vertex refraction zigzag, no arm ribbons. Three overlapping lobes break the
   * ellipse so the outline reads as an animal rather than a stain with an equation.
   */
  private updateKrakenShadow(
    view: VesselView,
    v: Vessel,
    surfaceAt: (x: number, z: number) => number,
    simTime: number,
    presence: KrakenPresence,
    kraken: { phase: string; timer: number } | null,
  ): void {
    // The fall owns the end: a painted mass lingering while the body slides under would
    // outlive the animal it stands for.
    const dead = kraken?.phase === 'dying' || kraken?.phase === 'gone';
    const alpha = dead ? 0 : presence.shadowAlpha;
    if (alpha <= 0.005) {
      if (view.submergedMesh) view.submergedMesh.visibility = 0;
      return;
    }

    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const fx = Math.sin(v.heading);
    const fz = Math.cos(v.heading);
    const rx = Math.cos(v.heading);
    const rz = -Math.sin(v.heading);
    const driftX = Math.sin(simTime * 0.071 + v.id * 0.61) * 14;
    const driftZ = Math.sin(simTime * 0.053 + v.id * 1.13) * 20;

    const addVertex = (localX: number, localZ: number, a: number, bulge: number): number => {
      const x = v.pos.x + fx * localZ + rx * localX;
      const z = v.pos.z + fz * localZ + rz * localX;
      const vertex = positions.length / 3;
      // Just proud of the swell: enough that Gerstner crests cannot slice the patch
      // into islands, low enough that it never reads as a plate sitting on the water.
      positions.push(x, surfaceAt(x, z) + 1.6 + bulge, z);
      // The sea's own colour a step darker and near-opaque — a patch that swallows the
      // sparkle and chop, not a painted shape. (The old pale-at-distance crossover is
      // gone with the overlay it belonged to.)
      colors.push(0.050, 0.085, 0.115, Math.min(1, a));
      return vertex;
    };

    const addLobe = (
      centreX: number,
      centreZ: number,
      radiusX: number,
      radiusZ: number,
      lobeAlpha: number,
      seed: number,
    ): void => {
      const segments = 30;
      // One smooth falloff, no visible bands: alpha follows (1 - f)^1.6 through enough
      // rings that adjacent steps are below what the eye separates over dark water.
      const fractions = [0.28, 0.52, 0.74, 0.9, 1];
      const centre = addVertex(centreX, centreZ, lobeAlpha, presence.bulgeHeight);
      const ringStarts: number[] = [];
      for (const fraction of fractions) {
        ringStarts.push(positions.length / 3);
        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * Math.PI * 2;
          // The torn fractal rim carries the organic outline on its own — static, so it
          // can never facet the way the animated refraction rim did.
          const torn = 1
            + Math.sin(i * 1.91 + seed) * 0.06
            + Math.sin(i * 3.47 - seed * 1.3) * 0.03;
          const localX = centreX + Math.cos(angle) * radiusX * fraction * torn;
          const localZ = centreZ + Math.sin(angle) * radiusZ * fraction * torn;
          const radialBulge = presence.bulgeHeight * (1 - fraction) * (1 - fraction);
          addVertex(localX, localZ, lobeAlpha * Math.pow(1 - fraction, 1.6), radialBulge);
        }
      }
      const inner = ringStarts[0] as number;
      for (let i = 0; i < segments; i++) indices.push(centre, inner + i, inner + i + 1);
      for (let ring = 0; ring < ringStarts.length - 1; ring++) {
        const a = ringStarts[ring] as number;
        const b = ringStarts[ring + 1] as number;
        for (let i = 0; i < segments; i++) {
          indices.push(a + i, b + i, a + i + 1, a + i + 1, b + i, b + i + 1);
        }
      }
    };

    const scale = presence.shadowScale;
    addLobe(driftX, -52 + driftZ, 228 * scale, 475 * scale, alpha, 0.7);
    addLobe(-126 + driftX * 0.6, 112 + driftZ * 0.4, 150 * scale, 292 * scale, alpha * 0.5, 2.3);
    addLobe(112 - driftX * 0.35, -222 - driftZ * 0.3, 166 * scale, 264 * scale, alpha * 0.45, 4.8);

    if (!view.submergedMesh) {
      view.submergedMesh = new BabylonMesh(`kraken_submerged_${v.id}`, this.scene);
      view.submergedMesh.material = this.krakenShadowMaterial;
      view.submergedMesh.isPickable = false;
      view.submergedMesh.alwaysSelectAsActiveMesh = true;
      view.submergedMesh.useVertexColors = true;
      view.submergedMesh.hasVertexAlpha = true;
    }
    view.submergedMesh.visibility = 1;

    const data = new VertexData();
    data.positions = positions;
    data.indices = indices;
    data.colors = colors;
    // Computed, not hardcoded up: real normals let the dome's slopes take the sky's
    // grazing light like a wet back instead of lighting as a flat sheet.
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);
    data.normals = normals;
    data.applyToMesh(view.submergedMesh, true);
  }

  /**
   * A short pressure wave wraps the bow, while tentacles churn a broken ring at their
   * planted base. Both are small pieces of water-contact geometry rather than particles,
   * keeping them local to the vessel system and deterministic in frozen captures.
   */
  private updateContactFoam(
    view: VesselView,
    v: Vessel,
    surfaceAt: (x: number, z: number) => number,
    simTime: number,
    predation?: KrakenPredationPose,
    tentacleGrip?: KrakenTentacleGrip | null,
    rigGrip?: KrakenRigGrip | null,
  ): void {
    if (v.state === 'dead') {
      if (view.contactMesh) view.contactMesh.visibility = 0;
      return;
    }

    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const uvs: number[] = [];

    if (v.kind === 'kraken') {
      const presence = krakenPresence(Math.hypot(v.pos.x, v.pos.z), v.submerged);
      const surfaced = presence.bodyVisibility;
      /*
       * The boil belongs to a surfaced animal only.
       *
       * It used to be `max(surfaced, wakeAlpha * 0.72)`, so a submerged mass still drew
       * its full perimeter — a white ellipse ring several hundred metres across, lying
       * on the water around nothing. Adam, having played it: "there are these white
       * rings around it — perhaps supposed to represent foam, but they're not good —
       * remove them." A thing that is under the water does not heave water off its back;
       * the displaced volume is the bulge and the shadow, which do that job already.
       */
      const pressureStrength = surfaced;
      /*
       * How much every white-water cue stands down while the mass is close *and* under.
       * `surfaced` is bodyVisibility, so the moment the animal comes up its boil, crests
       * and limb rings return to full strength untouched — this only quiets the water
       * over a thing that is still hiding beneath it.
       */
      const subDark = presence.shadowDarkness * (1 - surfaced);
      const segments = 48;
      const fx = Math.sin(v.heading);
      const fz = Math.cos(v.heading);
      const rx = Math.cos(v.heading);
      const rz = -Math.sin(v.heading);
      const pulse = 1 + Math.sin(simTime * 0.31 + v.id) * 0.025;
      const foamLift = 0.30 + (1 - surfaced) * (1.45 + presence.clarity * 1.25);

      for (let i = 0; i <= segments; i++) {
        const a = (i / segments) * Math.PI * 2;
        /*
         * The boil breaks into discrete wedges, which is right for a surfaced animal and
         * is precisely what made a close submerged mass read as pack ice: hard-edged
         * bright patches in a ring. Close in it evens out into a broad disturbance.
         */
        const brokenFar = 0.55 + 0.45 * Math.sin(i * 2.71 + v.id * 0.8);
        const brokenNear = 0.42 + 0.10 * Math.sin(i * 0.83 + v.id * 0.8);
        const broken = brokenFar * (1 - subDark) + brokenNear * subDark;
        const scale = surfaced + (1 - surfaced) * presence.shadowScale;
        const along = Math.cos(a) * KRAKEN_VISUAL_LENGTH * 0.48 * pulse * scale;
        const across = Math.sin(a) * KRAKEN_VISUAL_BEAM * 0.57 * pulse * scale;
        const width = (23 + 17 * (0.5 + 0.5 * Math.sin(i * 1.93)))
          * (0.72 + pressureStrength * 0.48);

        for (const edge of [-1, 1]) {
          const widenedAlong = along + Math.cos(a) * width * edge;
          const widenedAcross = across + Math.sin(a) * width * edge;
          const x = v.pos.x + fx * widenedAlong + rx * widenedAcross;
          const z = v.pos.z + fz * widenedAlong + rz * widenedAcross;
          positions.push(x, surfaceAt(x, z) + foamLift, z);
          /*
           * The boil pulls back as the mass comes close.
           *
           * Pure white at full strength is right for a surfaced animal heaving water off
           * its back, and wrong for something still under: at a kilometre it drew a ring
           * of bright broken patches around a dark centre, which is the exact recipe for
           * reading as pack ice rather than as a shadow. Close in the displaced water is
           * the bulge, not the foam.
           */
          const foamTone = 1 - subDark * 0.45;
          colors.push(
            foamTone, foamTone, foamTone,
            (edge < 0 ? 0.12 : 0.78) * broken * pressureStrength * (1 - subDark * 0.85),
          );
          uvs.push(i / segments, edge < 0 ? 0 : 1);
        }

        if (i < segments) {
          const base = i * 2;
          indices.push(base, base + 3, base + 1, base, base + 2, base + 3);
        }
      }

      // Sparse curtains break the otherwise dry silhouette where the mantle heaves out
      // of the water. They follow the same asymmetric perimeter as the boil, taper as
      // they fall, and vary enough that the edge never becomes a white picket fence.
      if (surfaced > 0.55) {
        const curtainCount = 11;
        for (let curtain = 0; curtain < curtainCount; curtain++) {
          const a = (
            (curtain / curtainCount) * Math.PI * 2
            + 0.13 * Math.sin(curtain * 2.43 + v.id)
          );
          const along = Math.cos(a) * KRAKEN_VISUAL_LENGTH * 0.49 * pulse;
          const across = Math.sin(a) * KRAKEN_VISUAL_BEAM * 0.58 * pulse;
          const centreX = v.pos.x + fx * along + rx * across;
          const centreZ = v.pos.z + fz * along + rz * across;
          const localTangentAlong = -Math.sin(a) * KRAKEN_VISUAL_LENGTH * 0.49;
          const localTangentAcross = Math.cos(a) * KRAKEN_VISUAL_BEAM * 0.58;
          const tangentX = fx * localTangentAlong + rx * localTangentAcross;
          const tangentZ = fz * localTangentAlong + rz * localTangentAcross;
          const tangentLength = Math.max(0.001, Math.hypot(tangentX, tangentZ));
          const tx = tangentX / tangentLength;
          const tz = tangentZ / tangentLength;
          const lowerWidth = 13 + (curtain % 4) * 5.2;
          const upperWidth = lowerWidth * (0.18 + (curtain % 3) * 0.07);
          const height = (
            12
            + (curtain % 5) * 4.4
            + Math.max(0, Math.sin(simTime * 0.17 + curtain * 1.31)) * 7
          ) * surfaced;
          const baseY = surfaceAt(centreX, centreZ) + 0.55;
          const curtainBase = positions.length / 3;

          positions.push(
            centreX - tx * lowerWidth, baseY, centreZ - tz * lowerWidth,
            centreX + tx * lowerWidth, baseY, centreZ + tz * lowerWidth,
            centreX - tx * upperWidth, baseY + height, centreZ - tz * upperWidth,
            centreX + tx * upperWidth, baseY + height, centreZ + tz * upperWidth,
          );
          const alpha = (0.46 + (curtain % 3) * 0.10) * surfaced;
          colors.push(
            1, 1, 1, alpha,
            1, 1, 1, alpha * 0.84,
            1, 1, 1, 0.025,
            1, 1, 1, 0.018,
          );
          uvs.push(0, 0, 1, 0, 0.42, 1, 0.58, 1);
          indices.push(
            curtainBase, curtainBase + 1, curtainBase + 2,
            curtainBase + 2, curtainBase + 1, curtainBase + 3,
          );
        }
      }

      // A submerged kilometre-wide body announces itself at the horizon through water,
      // not through visible flesh. Three broken pressure crests stand only a few metres
      // high at 10 km and grow toward the rig. They share this mesh/material, so the cue
      // costs no extra draw and survives the grazing view that hides a flat shadow.
      const crestSegments = 18;
      for (let row = 0; row < 3; row++) {
        const base = positions.length / 3;
        const forward = KRAKEN_VISUAL_LENGTH * (0.42 - row * 0.055) * presence.shadowScale;
        const halfWidth = KRAKEN_VISUAL_BEAM * (0.48 + row * 0.08) * presence.shadowScale;
        for (let i = 0; i <= crestSegments; i++) {
          const across = (i / crestSegments * 2 - 1) * halfWidth;
          const torn = Math.sin(i * 2.61 + row * 1.73 + simTime * 0.06);
          const localForward = forward + torn * (7 + row * 3);
          const x = v.pos.x + fx * localForward + rx * across;
          const z = v.pos.z + fz * localForward + rz * across;
          const baseY = surfaceAt(x, z) + 0.55;
          const broken = Math.max(0, Math.sin(i * 2.17 + row * 2.4 + simTime * 0.11));
          /*
           * The crests are the horizon's announcement — broken white water standing off
           * a distant submerged mass. By a kilometre the dome has taken over that job,
           * and fourteen-metre serrated curtains in front of it were the last thing
           * still reading as pack ice. They kneel as the mass closes.
           */
          const height = (5.5 + presence.clarity * 8.5) * (0.45 + broken * 0.55)
            * (1 - subDark * 0.6);
          /*
           * The one white-water cue a submerged mass keeps, and only at the horizon.
           *
           * This is the first tell in the whole encounter — a kilometre-wide body at
           * 10 km is otherwise nothing at a grazing view. `1 - clarity` is the distance
           * fade: full strength where the shape is unreadable, gone by a kilometre,
           * which is exactly where the rings Adam objected to were being seen.
           */
          const crestStrength = Math.max(
            surfaced,
            presence.wakeAlpha * (1 - presence.clarity),
          );
          const alpha = crestStrength * broken * (0.18 - row * 0.035) * (1 - subDark * 0.88);
          positions.push(x, baseY, z, x, baseY + height, z);
          colors.push(1, 1, 1, alpha * 0.18, 1, 1, 1, alpha);
          uvs.push(i / crestSegments, 0, i / crestSegments, 1);
          if (i < crestSegments) {
            const crestBase = base + i * 2;
            indices.push(
              crestBase, crestBase + 2, crestBase + 1,
              crestBase + 1, crestBase + 2, crestBase + 3,
            );
          }
        }
      }

      // The broad boil says "enormous body"; these eight pressure rings say "moving
      // limbs". Their centres use the exact authored arm paths and the same deterministic
      // offsets as the mesh, so the water response follows each contact instead of
      // pulsing as a detached decorative ellipse.
      const ringSegments = 12;
      /*
       * Also surfaced-only, and these were the worst of it: eight discrete white rings
       * standing on the water around a body nobody can see. At range they were meant to
       * read as limbs working under the surface; in play they read as eight painted
       * circles. The submerged animal keeps the shadow and the dome and nothing white.
       */
      const limbPressure = surfaced;
      for (let arm = 0; arm < KRAKEN_ARM_WATER_CONTACTS.length; arm++) {
        if (limbPressure <= 0.025) break;
        const contact = KRAKEN_ARM_WATER_CONTACTS[arm];
        if (!contact) continue;
        const progress = KRAKEN_ARM_CONTACT_PROGRESS[arm] as number;
        const motion = krakenArmOffset(arm, progress, simTime, v.state === 'attacking');
        const localX = contact.x + motion.x;
        const localZ = contact.z + motion.z;
        const centreX = v.pos.x + fx * localZ + rx * localX;
        const centreZ = v.pos.z + fz * localZ + rz * localX;
        const skylineBreach = progress < 0.2;
        const radius = (skylineBreach ? 38 : 27)
          + (arm % 3) * 7
          + Math.abs(motion.y) * 0.32;
        const width = (skylineBreach ? 13 : 9) + (arm % 2) * 3;
        const base = positions.length / 3;

        for (let i = 0; i <= ringSegments; i++) {
          const a = (i / ringSegments) * Math.PI * 2;
          const broken = 0.58 + 0.42 * Math.sin(i * 3.17 + arm * 1.91 + simTime * 0.22);
          const tornEdge = Math.sin(i * 2.31 + arm * 1.17) * width * 0.34;
          for (const edge of [-1, 1]) {
            const r = radius + width * edge + tornEdge;
            const x = centreX + Math.cos(a) * r;
            const z = centreZ + Math.sin(a) * r;
            positions.push(x, surfaceAt(x, z) + foamLift + 0.04, z);
            colors.push(
              1, 1, 1,
              (edge < 0 ? 0.16 : skylineBreach ? 0.88 : 0.68)
                * broken * limbPressure * (1 - subDark * 0.6),
            );
            uvs.push(i / ringSegments, edge < 0 ? 0 : 1);
          }
          if (i < ringSegments) {
            const ringBase = base + i * 2;
            indices.push(
              ringBase, ringBase + 3, ringBase + 1,
              ringBase, ringBase + 2, ringBase + 3,
            );
          }
        }

        // The two skyline limbs leave thick water curtains where hundreds of metres of
        // flesh pull free of the sea. Unequal, tapered sheets share the contact mesh, so
        // they cost no draw calls and remain locked to the exact animated limb crossing.
        if (skylineBreach && surfaced > 0.55) {
          const sheetCount = arm === 0 ? 7 : 5;
          for (let sheet = 0; sheet < sheetCount; sheet++) {
            const a = (
              (sheet / sheetCount) * Math.PI * 2
              + arm * 0.71
              + Math.sin(sheet * 2.13 + arm) * 0.18
            );
            const distance = radius * (0.56 + (sheet % 3) * 0.13);
            const sheetX = centreX + Math.cos(a) * distance;
            const sheetZ = centreZ + Math.sin(a) * distance;
            const tangentX = -Math.sin(a);
            const tangentZ = Math.cos(a);
            const lowerWidth = 8 + (sheet % 4) * 3.2;
            const upperWidth = lowerWidth * (0.24 + (sheet % 2) * 0.10);
            const height = (
              9
              + (arm === 0 ? 12 : 6)
              + (sheet % 3) * 4.2
              + Math.max(0, Math.sin(simTime * 0.19 + sheet * 1.7)) * 5
            ) * surfaced;
            const baseY = surfaceAt(sheetX, sheetZ) + 0.45;
            const sheetBase = positions.length / 3;

            positions.push(
              sheetX - tangentX * lowerWidth, baseY, sheetZ - tangentZ * lowerWidth,
              sheetX + tangentX * lowerWidth, baseY, sheetZ + tangentZ * lowerWidth,
              sheetX - tangentX * upperWidth, baseY + height, sheetZ - tangentZ * upperWidth,
              sheetX + tangentX * upperWidth, baseY + height, sheetZ + tangentZ * upperWidth,
            );
            const sheetAlpha = (0.48 + (sheet % 3) * 0.10) * surfaced;
            colors.push(
              1, 1, 1, sheetAlpha,
              1, 1, 1, sheetAlpha * 0.82,
              1, 1, 1, 0.035,
              1, 1, 1, 0.025,
            );
            uvs.push(0, 0, 1, 0, 0.38, 1, 0.62, 1);
            indices.push(
              sheetBase, sheetBase + 1, sheetBase + 2,
              sheetBase + 2, sheetBase + 1, sheetBase + 3,
            );
          }
        }
      }

    } else if (v.kind === 'tentacle') {
      // A broad alpha band turned into a ring of white paving slabs at rig distance.
      // Several narrow, independently broken filaments now occupy the same footprint:
      // their gaps show dark water between strands and their radii wander continuously.
      const segments = 52;
      const addBrokenRing = (
        centreX: number,
        centreZ: number,
        radius: number,
        width: number,
        strength: number,
        seed: number,
      ): void => {
        for (let lane = 0; lane < 3; lane++) {
          const laneOffset = (lane - 1) * width * 0.68;
          const laneStrength = 1 - Math.abs(lane - 1) * 0.13;
          for (let i = 0; i < segments; i++) {
            const a0 = (i / segments) * Math.PI * 2;
            const a1 = ((i + 1) / segments) * Math.PI * 2;
            const foamAt = (a: number): number => {
              const coarse = 0.52 + 0.48 * Math.sin(
                a * (5 + lane) + seed * 1.31 + simTime * 0.17,
              );
              const tear = 0.5 + 0.5 * Math.sin(a * (11 - lane) - seed * 0.73);
              return Math.pow(Math.max(0, coarse * 0.82 + tear * 0.34 - 0.43), 1.3);
            };
            const foam0 = foamAt(a0);
            const foam1 = foamAt(a1);
            if (Math.max(foam0, foam1) < 0.075) continue;

            const radialAt = (a: number): number => (
              radius
              + laneOffset
              + Math.sin(a * 3 + seed + lane * 0.9) * width * 0.72
              + Math.sin(a * 7 - seed * 0.4) * width * 0.26
            );
            const r0 = radialAt(a0);
            const r1 = radialAt(a1);
            const half0 = width * (0.075 + foam0 * 0.045);
            const half1 = width * (0.075 + foam1 * 0.045);
            const x0i = centreX + Math.cos(a0) * (r0 - half0);
            const z0i = centreZ + Math.sin(a0) * (r0 - half0);
            const x0o = centreX + Math.cos(a0) * (r0 + half0);
            const z0o = centreZ + Math.sin(a0) * (r0 + half0);
            const x1i = centreX + Math.cos(a1) * (r1 - half1);
            const z1i = centreZ + Math.sin(a1) * (r1 - half1);
            const x1o = centreX + Math.cos(a1) * (r1 + half1);
            const z1o = centreZ + Math.sin(a1) * (r1 + half1);
            const base = positions.length / 3;

            positions.push(
              x0i, surfaceAt(x0i, z0i) + 0.32, z0i,
              x0o, surfaceAt(x0o, z0o) + 0.34, z0o,
              x1i, surfaceAt(x1i, z1i) + 0.32, z1i,
              x1o, surfaceAt(x1o, z1o) + 0.34, z1o,
            );
            const alpha0 = foam0 * strength * laneStrength;
            const alpha1 = foam1 * strength * laneStrength;
            colors.push(
              1, 1, 1, alpha0 * 0.34,
              1, 1, 1, alpha0 * 0.72,
              1, 1, 1, alpha1 * 0.34,
              1, 1, 1, alpha1 * 0.72,
            );
            uvs.push(0, 0, 0, 1, 1, 0, 1, 1);
            indices.push(
              base, base + 3, base + 1,
              base, base + 2, base + 3,
            );
          }
        }
      };

      const rigLoad = rigGrip?.grip ?? 0;
      const rigSlam = rigGrip?.slam ?? 0;
      const pulse = 1 + Math.sin(simTime * 0.43 + v.id) * 0.055;
      addBrokenRing(
        v.pos.x,
        v.pos.z,
        (18 + rigLoad * 4 + rigSlam * 7) * pulse,
        2.6 + rigLoad * 1.4,
        Math.min(1, 0.82 + rigLoad * 0.14),
        v.id,
      );
      addBrokenRing(
        v.pos.x,
        v.pos.z,
        (29 + rigLoad * 9 + rigSlam * 11) * pulse,
        3.8 + rigLoad * 1.8,
        Math.min(1, 0.56 + rigLoad * 0.30),
        v.id + 4.1,
      );

      if (rigGrip && rigGrip.grip > 0.04) {
        // The root does not become magically dry once the limb has climbed the jacket.
        // A second broken boil surrounds the actual leg-water intersection, while tall
        // torn sheets bridge the distance back to the planted limb. The short slam beat
        // temporarily enlarges both, making the steel load visible through the sea.
        addBrokenRing(
          rigGrip.legWaterX,
          rigGrip.legWaterZ,
          14 + rigGrip.grip * 7 + rigGrip.slam * 9,
          3.4 + rigGrip.grip * 1.6,
          Math.min(1, rigGrip.grip * (0.72 + rigGrip.slam * 0.34)),
          v.id + 12.7,
        );

        const c = Math.cos(rigGrip.heading);
        const s = Math.sin(rigGrip.heading);
        const strandCount = 15;
        for (let strand = 0; strand < strandCount; strand++) {
          const progress = (strand + 0.5) / strandCount;
          const limbProgress = 0.30 + progress * 0.54;
          const local = krakenRigWrapPoint(rigGrip, limbProgress);
          const sourceX = v.pos.x + c * local.x + s * local.z;
          const sourceY = v.pos.y + local.y;
          const sourceZ = v.pos.z - s * local.x + c * local.z;
          const waterY = surfaceAt(sourceX, sourceZ) + 0.48;
          if (sourceY < waterY + 2.5) continue;
          /*
           * Runoff belongs to the breach, not the whole limb. The old leg wrap stood
           * 28 m from the camera and its falling water read as spray; the reared wrap
           * carries points sixty metres up at a hundred metres out, and curtains from
           * that height rendered as huge glass panes hanging in the air. A limb sheds
           * the sea only where it just left it.
           */
          if (sourceY > waterY + 15) continue;

          const sideAngle = rigGrip.approachAngle
            + rigGrip.coilSign * progress * Math.PI * 1.4;
          const sideX = Math.cos(sideAngle);
          const sideZ = Math.sin(sideAngle);
          const sourceOffset = (5.8 + (strand % 4) * 1.5)
            * (strand % 2 === 0 ? -1 : 1);
          const topX = sourceX + sideX * sourceOffset;
          const topZ = sourceZ + sideZ * sourceOffset;
          const fall = Math.min(
            sourceY - waterY,
            12 + (strand % 5) * 4.8 + rigGrip.slam * 10,
          );
          const bottomY = Math.max(waterY, sourceY - fall);
          const driftX = Math.sin(strand * 1.91 + v.id) * (2.2 + rigGrip.slam * 2.4);
          const driftZ = Math.cos(strand * 1.37 - v.id) * (1.8 + rigGrip.slam * 2.0);
          const slices = 4;
          const base = positions.length / 3;

          for (let slice = 0; slice <= slices; slice++) {
            const q = slice / slices;
            const sway = Math.sin(q * Math.PI) * Math.sin(strand * 2.17 + v.id) * 1.6;
            const x = topX + driftX * q + sideX * sway;
            const y = sourceY + (bottomY - sourceY) * q;
            const z = topZ + driftZ * q + sideZ * sway;
            const halfWidth = (
              1.25
              + (strand % 3) * 0.42
              + rigGrip.slam * 0.55
            ) * (1 - q * 0.74);
            const acrossX = -sideZ;
            const acrossZ = sideX;
            positions.push(
              x - acrossX * halfWidth, y, z - acrossZ * halfWidth,
              x + acrossX * halfWidth, y, z + acrossZ * halfWidth,
            );
            const middle = Math.sin(q * Math.PI);
            const alpha = (
              0.10 + middle * (0.44 + (strand % 3) * 0.08)
            ) * rigGrip.grip;
            colors.push(
              1, 1, 1, alpha * 0.72,
              1, 1, 1, alpha,
            );
            uvs.push(q, 0, q, 1);
            if (slice < slices) {
              const strip = base + slice * 2;
              indices.push(
                strip, strip + 3, strip + 1,
                strip, strip + 2, strip + 3,
              );
            }
          }
        }
      }

      if (tentacleGrip && tentacleGrip.grip > 0.08) {
        const c = Math.cos(v.heading);
        const s = Math.sin(v.heading);
        const worldFromLocal = (localX: number, localZ: number): [number, number] => [
          v.pos.x + c * localX + s * localZ,
          v.pos.z - s * localX + c * localZ,
        ];
        const [stationX, stationZ] = worldFromLocal(
          tentacleGrip.stationLocalX,
          tentacleGrip.stationLocalZ,
        );
        addBrokenRing(
          stationX,
          stationZ,
          tentacleGrip.halfBeam + 9,
          7 + tentacleGrip.grip * 5,
          tentacleGrip.grip,
          v.id + 8.7,
        );

        const [contactX, contactZ] = worldFromLocal(
          tentacleGrip.localX,
          tentacleGrip.localZ,
        );
        const forwardX = c * tentacleGrip.forwardLocalX + s * tentacleGrip.forwardLocalZ;
        const forwardZ = -s * tentacleGrip.forwardLocalX + c * tentacleGrip.forwardLocalZ;
        for (let sheet = 0; sheet < 9; sheet++) {
          const offset = (sheet - 4) * (2.8 + tentacleGrip.halfBeam * 0.035);
          const x = contactX + forwardX * offset;
          const z = contactZ + forwardZ * offset;
          const halfWidth = 1.2 + (sheet % 3) * 0.65;
          const height = (7 + (sheet % 4) * 4.2) * tentacleGrip.grip;
          const baseY = surfaceAt(x, z) + 0.42;
          const sheetBase = positions.length / 3;
          positions.push(
            x - forwardX * halfWidth, baseY, z - forwardZ * halfWidth,
            x + forwardX * halfWidth, baseY, z + forwardZ * halfWidth,
            x - forwardX * halfWidth * 0.22, baseY + height, z - forwardZ * halfWidth * 0.22,
            x + forwardX * halfWidth * 0.22, baseY + height, z + forwardZ * halfWidth * 0.22,
          );
          const alpha = (0.52 + (sheet % 2) * 0.16) * tentacleGrip.grip;
          colors.push(
            1, 1, 1, alpha,
            1, 1, 1, alpha * 0.80,
            1, 1, 1, 0.025,
            1, 1, 1, 0.018,
          );
          uvs.push(0, 0, 1, 0, 0.42, 1, 0.58, 1);
          indices.push(
            sheetBase, sheetBase + 1, sheetBase + 2,
            sheetBase + 2, sheetBase + 1, sheetBase + 3,
          );
        }
      }
    } else {
      const spec = VESSEL_SPECS[v.kind];
      const settling = v.state === 'sinking'
        ? Math.min(1, Math.max(
          0,
          (v.stateTimer - spec.smokeDuration) / spec.sinkDuration,
        ))
        : 0;
      if (v.speed < 0.6 && !predation && settling <= 0) {
        if (view.contactMesh) view.contactMesh.visibility = 0;
        return;
      }

      const fx = Math.sin(v.heading);
      const fz = Math.cos(v.heading);
      const rx = Math.cos(v.heading);
      const rz = -Math.sin(v.heading);
      const submergedSub = v.kind === 'minisub' && v.submerged;
      const bow = submergedSub ? 1.8 : spec.length * 0.48;
      const length = submergedSub ? 6.5 : Math.max(3.5, spec.beam * 1.15);
      const width = submergedSub ? 0.72 : spec.beam * 0.54;

      const point = (forward: number, right: number): [number, number, number] => {
        const x = v.pos.x + fx * forward + rx * right;
        const z = v.pos.z + fz * forward + rz * right;
        return [x, surfaceAt(x, z) + 0.24, z];
      };

      if (settling <= 0) {
        for (const side of [-1, 1]) {
          const base = positions.length / 3;
          const points = [
            point(bow + length * 0.18, side * width * 0.10),
            point(bow - length * 0.10, side * width * 0.48),
            point(bow - length * 0.10, side * width * 0.78),
            point(bow - length, side * width * 0.88),
            point(bow - length, side * width * 1.34),
          ];
          for (let i = 0; i < points.length; i++) {
            positions.push(...(points[i] as [number, number, number]));
            const alpha = [0.12, 0.58, 0.72, 0.06, 0.14][i] as number;
            colors.push(1, 1, 1, alpha * Math.min(1, v.speed / 9));
            uvs.push(i / 4, side < 0 ? 0 : 1);
          }
          indices.push(
            base, base + 2, base + 1,
            base + 1, base + 2, base + 4,
            base + 1, base + 4, base + 3,
          );
        }
      } else {
        /*
         * A settling wreck displaces the sea even after its engines and bow wake stop.
         * Two torn ellipses close over the shrinking footprint as the hull descends.
         * They reuse the vessel's contact-mesh path and shared foam materials. That is
         * one transient contact draw for a visible wreck, not a new particle system or
         * permanent material.
         */
        const ringSegments = spec.length > 100 ? 48 : 40;
        const ringCount = spec.length > 40 ? 2 : 1;
        const fadeIn = Math.min(1, settling / 0.10);
        const fadeOut = Math.min(1, (1 - settling) / 0.12);
        const strength = Math.sqrt(fadeIn * fadeOut);

        for (let ring = 0; ring < ringCount; ring++) {
          const base = positions.length / 3;
          const alongRadius = spec.length * (0.48 - settling * 0.10 + ring * 0.035);
          const acrossRadius = spec.beam * (0.72 - settling * 0.12 + ring * 0.14);
          const ringWidth = Math.max(0.35, spec.beam * 0.055)
            * (1 + settling * 0.85 + ring * 0.28);

          for (let i = 0; i <= ringSegments; i++) {
            const a = (i / ringSegments) * Math.PI * 2;
            const torn = Math.sin(i * 2.31 + ring * 1.77 + v.id * 0.63 + simTime * 0.17);
            const broken = 0.52 + 0.48 * Math.max(
              0,
              Math.sin(i * 2.87 + ring * 2.11 + v.id),
            );
            const along = Math.cos(a) * alongRadius;
            const across = Math.sin(a) * acrossRadius;

            for (const edge of [-1, 1]) {
              const edgeOffset = edge * ringWidth + torn * ringWidth * 0.34;
              const localAlong = along + Math.cos(a) * edgeOffset;
              const localAcross = across + Math.sin(a) * edgeOffset;
              const x = v.pos.x + fx * localAlong + rx * localAcross;
              const z = v.pos.z + fz * localAlong + rz * localAcross;
              positions.push(x, surfaceAt(x, z) + 0.30 + ring * 0.04, z);
              const alpha = (edge < 0 ? 0.16 : 0.86)
                * broken * strength * (1 - ring * 0.28);
              // Slightly above white in linear space: aerated water stays readable after
              // the ordinary foam material passes through distance fog and ACES.
              colors.push(1.22, 1.22, 1.22, alpha);
              uvs.push(i / ringSegments, edge < 0 ? 0 : 1);
            }

            if (i < ringSegments) {
              const ringBase = base + i * 2;
              indices.push(
                ringBase, ringBase + 3, ringBase + 1,
                ringBase, ringBase + 2, ringBase + 3,
              );
            }
          }
        }
      }

      if (predation) {
        /*
         * A ship being lifted and pulled cannot keep its ordinary bow wake. Three torn
         * ellipses boil around the whole displacement footprint, then tighten as the
         * hull is dragged down. This shares the vessel's contact mesh and foam material,
         * so the physical read costs no additional draw call.
         */
        const ringSegments = 28;
        const centreX = v.pos.x + predation.offsetX;
        const centreZ = v.pos.z + predation.offsetZ;
        for (let ring = 0; ring < 3; ring++) {
          const base = positions.length / 3;
          const alongRadius = spec.length * (0.50 + ring * 0.075);
          const acrossRadius = spec.beam * (0.82 + ring * 0.34)
            + predation.attackerCount * 2.5;
          const ringWidth = 3.6 + ring * 2.2 + predation.contact * 3.5;

          for (let i = 0; i <= ringSegments; i++) {
            const a = (i / ringSegments) * Math.PI * 2;
            const torn = Math.sin(i * 2.43 + ring * 1.81 + simTime * 0.19);
            const along = Math.cos(a) * alongRadius;
            const across = Math.sin(a) * acrossRadius;
            for (const edge of [-1, 1]) {
              const edgeOffset = edge * ringWidth + torn * ringWidth * 0.32;
              const localAlong = along + Math.cos(a) * edgeOffset;
              const localAcross = across + Math.sin(a) * edgeOffset;
              const x = centreX + fx * localAlong + rx * localAcross;
              const z = centreZ + fz * localAlong + rz * localAcross;
              positions.push(x, surfaceAt(x, z) + 0.34 + predation.drag * 0.8, z);
              const broken = 0.50 + 0.50 * Math.max(
                0,
                Math.sin(i * 3.17 + ring * 2.2 + v.id),
              );
              const alpha = (edge < 0 ? 0.10 : 0.68)
                * broken * predation.contact * (1 - ring * 0.18);
              colors.push(1, 1, 1, alpha);
              uvs.push(i / ringSegments, edge < 0 ? 0 : 1);
            }
            if (i < ringSegments) {
              const ringBase = base + i * 2;
              indices.push(
                ringBase, ringBase + 3, ringBase + 1,
                ringBase, ringBase + 2, ringBase + 3,
              );
            }
          }
        }
      }
    }

    if (!view.contactMesh) {
      view.contactMesh = new BabylonMesh(`contact_${v.id}`, this.scene);
      view.contactMesh.isPickable = false;
      view.contactMesh.alwaysSelectAsActiveMesh = true;
      view.contactMesh.useVertexColors = true;
      view.contactMesh.hasVertexAlpha = true;
    }
    const needsPressureVisibility = v.state === 'sinking'
      && VESSEL_SPECS[v.kind].length > 40;
    view.contactMesh.material = v.kind === 'kraken' || needsPressureVisibility
      ? this.krakenPressureMaterial
      : this.wakeMaterial;
    view.contactMesh.visibility = 1;

    const vd = new VertexData();
    vd.positions = positions;
    vd.indices = indices;
    vd.colors = colors;
    vd.uvs = uvs;
    vd.normals = new Array(positions.length).fill(0).map((_, i) => (i % 3 === 1 ? 1 : 0));
    vd.applyToMesh(view.contactMesh, true);
  }

  /**
   * Sample the vessel's track and rebuild its wake ribbon.
   *
   * The ribbon widens with age — a real wake spreads as it decays, and a constant-width
   * trail reads as a painted stripe. Alpha falls off on a curve so the tail dissolves
   * instead of ending on a hard edge.
   */
  private updateWake(
    view: VesselView,
    v: Vessel,
    dt: number,
    surfaceAt: (x: number, z: number) => number,
  ): void {
    if (v.kind === 'tentacle') {
      if (view.wakeMesh) view.wakeMesh.visibility = 0;
      return;
    }

    const spec = VESSEL_SPECS[v.kind];
    const moving = v.speed > 1.2 && v.state !== 'dead';

    view.wakeTimer += dt;
    if (moving && view.wakeTimer >= WAKE_INTERVAL) {
      view.wakeTimer = 0;
      // Sample just astern of the hull so the ribbon starts at the transom.
      const sx = v.pos.x - Math.sin(v.heading) * spec.length * 0.45;
      const sz = v.pos.z - Math.cos(v.heading) * spec.length * 0.45;
      const i = view.wakeHead * 3;
      view.wake[i] = sx;
      view.wake[i + 1] = v.heading;
      view.wake[i + 2] = sz;
      view.wakeHead = (view.wakeHead + 1) % WAKE_POINTS;
      view.wakeCount = Math.min(view.wakeCount + 1, WAKE_POINTS);
    }

    if (view.wakeCount < 3) return;

    // Walk the ring buffer oldest → newest.
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const uvs: number[] = [];

    const n = view.wakeCount;
    for (let k = 0; k < n; k++) {
      const idx = (view.wakeHead - n + k + WAKE_POINTS * 2) % WAKE_POINTS;
      const i = idx * 3;
      const x = view.wake[i] as number;
      const heading = view.wake[i + 1] as number;
      const z = view.wake[i + 2] as number;

      // age 0 = newest (at the stern), 1 = oldest (far astern).
      const age = 1 - k / (n - 1);

      // Kelvin geometry: the wake spreads at a fixed half-angle behind the hull, so
      // width grows with distance astern rather than with time alone.
      const spread = spec.beam * (0.55 + age * 3.4);
      const px = Math.cos(heading);
      const pz = -Math.sin(heading);

      const inner = spread * (0.40 + age * 0.08);
      /*
       * Fit the whole cross-section to the wave field, not just its centreline.
       *
       * A tanker's old wake is over a hundred metres wide — enough to cross several
       * Gerstner crests. Giving all four vertices the centre sample forced that broad
       * ribbon into one flat shelf, alternately hovering over and cutting through the
       * water beside it. Four samples are still a tiny bounded cost (84 sections at the
       * full history cap), and they use the exact same CPU wave field that floats the
       * hull and drives the ocean shader.
       */
      const wakePoint = (offset: number): [number, number, number] => {
        const wakeX = x + px * offset;
        const wakeZ = z + pz * offset;
        return [wakeX, surfaceAt(wakeX, wakeZ) + 0.22, wakeZ];
      };
      positions.push(...wakePoint(-spread));
      positions.push(...wakePoint(-inner));
      positions.push(...wakePoint(inner));
      positions.push(...wakePoint(spread));

      // Fade: strong at the stern, gone at the tail. Slower ships leave fainter wakes.
      const speedFactor = Math.min(1, v.speed / 9);
      const alpha = Math.pow(1 - age, 1.6) * 0.72 * speedFactor;
      colors.push(1, 1, 1, alpha * 0.24);
      colors.push(1, 1, 1, alpha * 0.72);
      colors.push(1, 1, 1, alpha * 0.72);
      colors.push(1, 1, 1, alpha * 0.24);
      uvs.push(0, age);
      uvs.push(0.42, age);
      uvs.push(0.58, age);
      uvs.push(1, age);

      if (k < n - 1) {
        const b = k * 4;
        indices.push(
          b, b + 4, b + 1,
          b + 1, b + 4, b + 5,
          b + 2, b + 6, b + 3,
          b + 3, b + 6, b + 7,
        );
      }
    }

    if (!view.wakeMesh) {
      view.wakeMesh = new BabylonMesh(`wake_${v.id}`, this.scene);
      view.wakeMesh.material = this.wakeMaterial;
      view.wakeMesh.isPickable = false;
      view.wakeMesh.alwaysSelectAsActiveMesh = true;
      view.wakeMesh.useVertexColors = true;
      view.wakeMesh.hasVertexAlpha = true;
    }
    view.wakeMesh.visibility = 1;

    const vd = new VertexData();
    vd.positions = positions;
    vd.indices = indices;
    vd.colors = colors;
    vd.uvs = uvs;
    // Flat ribbon lying on the water: a constant up-normal is correct and cheap.
    vd.normals = new Array(positions.length).fill(0).map((_, i) => (i % 3 === 1 ? 1 : 0));
    vd.applyToMesh(view.wakeMesh, true);
  }

  /** World position of a vessel's instance, for HUD projection. */
  positionOf(id: EntityId): Vector3 | null {
    return this.views.get(id)?.instance.position ?? null;
  }

  dispose(): void {
    for (const view of this.views.values()) {
      view.instance.dispose();
      view.wakeMesh?.dispose();
      view.contactMesh?.dispose();
      view.submergedMesh?.dispose();
    }
    this.views.clear();
    this.clearWreckTraces();
    this.prototypes.dispose();
    this.wakeMaterial.dispose();
    this.oilSlickMaterial.dispose();
    this.wreckDebrisMaterial.dispose();
    this.krakenShadowMaterial.dispose();
    this.krakenPressureMaterial.dispose();
    for (const material of new Set(Object.values(this.surfaceMaterials))) material.dispose();
    for (const texture of this.organicTextures) texture.dispose();
  }
}
